import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2, ClipboardList, Pencil, X, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Review queue for AI-extracted rows with confidence < 90%.
 * Supports inline editing per module before confirm.
 */
export const PendingImports = ({ clientId, API, module, onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const q = module ? `?module=${module}` : '';
      const res = await axios.get(`${API}/pending-imports/${clientId}${q}`);
      setItems(res.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, module]);

  const beginEdit = (item) => {
    setEditingId(item.id);
    setEditData({ ...item.row_data });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData(null);
  };

  const confirm = async (id, edited = null) => {
    setBusyId(id);
    try {
      const body = edited ? { row_data: edited } : {};
      await axios.post(`${API}/pending-imports/${id}/confirm`, body);
      setItems(items.filter(it => it.id !== id));
      setEditingId(null);
      setEditData(null);
      onChange && onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      alert('Failed to confirm: ' + detail);
    } finally { setBusyId(null); }
  };

  const reject = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/pending-imports/${id}/reject`);
      setItems(items.filter(it => it.id !== id));
      onChange && onChange();
    } catch (e) {
      alert('Failed to reject: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusyId(null); }
  };

  if (loading) return (
    <div className="bg-white border border-border rounded-md p-6 text-center text-text-muted text-sm">
      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading review queue...
    </div>
  );

  if (items.length === 0) return null;

  return (
    <div className="bg-white border border-state-warning/40 rounded-md overflow-hidden" data-testid={`pending-imports-${module || 'all'}`}>
      <div className="px-5 py-3 bg-yellow-50 border-b border-state-warning/40 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <ClipboardList className="h-4 w-4 text-state-warning" />
          <h4 className="font-semibold text-primary">
            {items.length} row{items.length > 1 ? 's' : ''} awaiting your review
          </h4>
        </div>
        <span className="text-[11px] text-text-muted">Confidence &lt; 90% — edit, confirm, or reject</span>
      </div>

      <div className="divide-y divide-border max-h-[640px] overflow-y-auto">
        {items.map(it => (
          <PendingRow
            key={it.id}
            item={it}
            busy={busyId === it.id}
            isEditing={editingId === it.id}
            editData={editingId === it.id ? editData : null}
            onEditChange={setEditData}
            onBeginEdit={() => beginEdit(it)}
            onCancelEdit={cancelEdit}
            onConfirm={(edited) => confirm(it.id, edited)}
            onReject={() => reject(it.id)}
          />
        ))}
      </div>
    </div>
  );
};

const PendingRow = ({ item, busy, isEditing, editData, onEditChange, onBeginEdit, onCancelEdit, onConfirm, onReject }) => {
  const row = item.row_data || {};
  const conf = item.confidence || row._confidence || 0;
  const confColor = conf >= 70 ? 'text-state-success' : conf >= 50 ? 'text-state-warning' : 'text-state-error';
  const summary = formatRow(item.module, row);
  const warnings = row._warnings || [];

  return (
    <div className="px-5 py-3 hover:bg-background-secondary" data-testid={`pending-row-${item.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0 pr-4">
          <div className="flex items-center space-x-2 mb-1 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider bg-background-secondary text-text-secondary px-1.5 py-0.5 rounded font-semibold">
              {item.module}
            </span>
            <span className={`text-xs font-bold ${confColor}`}>{conf}% conf.</span>
            <span className="text-[11px] text-text-muted truncate">from {item.filename}</span>
          </div>
          {!isEditing && (
            <>
              <p className="text-sm font-medium text-text-primary truncate">{summary.title}</p>
              <p className="text-xs text-text-secondary truncate">{summary.detail}</p>
            </>
          )}
        </div>
        <div className="flex items-center space-x-1 flex-shrink-0">
          {!isEditing ? (
            <>
              <Button onClick={onBeginEdit} disabled={busy} size="sm" variant="outline"
                className="text-primary hover:bg-background-secondary"
                data-testid={`edit-pending-${item.id}`}>
                <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
              <Button onClick={() => onConfirm()} disabled={busy} size="sm" variant="outline"
                className="border-state-success text-state-success hover:bg-green-50"
                data-testid={`confirm-pending-${item.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Confirm
              </Button>
              <Button onClick={onReject} disabled={busy} size="sm" variant="outline"
                className="border-state-error text-state-error hover:bg-red-50"
                data-testid={`reject-pending-${item.id}`}>
                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => onConfirm(editData)} disabled={busy} size="sm"
                className="bg-state-success hover:bg-state-success/90 text-white"
                data-testid={`save-confirm-pending-${item.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Save &amp; Confirm
              </Button>
              <Button onClick={onCancelEdit} disabled={busy} size="sm" variant="outline"
                data-testid={`cancel-edit-pending-${item.id}`}>
                <X className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      {warnings.length > 0 && !isEditing && (
        <div className="mt-2 flex flex-wrap gap-1">
          {warnings.map((w, i) => (
            <span key={i} className="text-[10px] bg-yellow-50 border border-state-warning/30 text-state-warning px-1.5 py-0.5 rounded inline-flex items-center">
              <AlertTriangle className="h-2.5 w-2.5 mr-1" /> {w}
            </span>
          ))}
        </div>
      )}

      {isEditing && (
        <div className="mt-3 bg-background-secondary border border-border rounded-md p-3" data-testid={`edit-form-${item.id}`}>
          <EditForm module={item.module} value={editData} onChange={onEditChange} />
        </div>
      )}
    </div>
  );
};

const EditForm = ({ module, value, onChange }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const FIELDS = FIELD_MAP[module] || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
      {FIELDS.map(f => (
        <Field key={f.key} field={f} value={value?.[f.key]}
          onChange={(v) => set(f.key, v)} testid={`edit-${module}-${f.key}`} />
      ))}
    </div>
  );
};

const Field = ({ field, value, onChange, testid }) => {
  const onChangeRaw = (e) => {
    const v = e.target.value;
    if (field.type === 'number') onChange(v === '' ? null : parseFloat(v));
    else onChange(v);
  };
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold">
        {field.label}
      </span>
      <input
        type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
        step={field.type === 'number' ? 'any' : undefined}
        value={value ?? ''}
        onChange={onChangeRaw}
        placeholder={field.placeholder || ''}
        className="w-full mt-0.5 border border-border rounded px-2 py-1 text-sm bg-white"
        data-testid={testid}
      />
    </label>
  );
};

const FIELD_MAP = {
  suppliers: [
    { key: 'supplier_name', label: 'Supplier', type: 'text' },
    { key: 'invoice_number', label: 'Invoice #', type: 'text' },
    { key: 'date', label: 'Date', type: 'date' },
    { key: 'item_name', label: 'Item', type: 'text' },
    { key: 'quantity', label: 'Quantity', type: 'number' },
    { key: 'unit', label: 'Unit', type: 'text', placeholder: 'kg/ltr/pcs' },
    { key: 'unit_price', label: 'Unit Price (₹)', type: 'number' },
    { key: 'total_amount', label: 'Total (₹)', type: 'number' },
  ],
  reconciliation: [
    { key: 'date', label: 'Date', type: 'date' },
    { key: 'expected_amount', label: 'Expected (₹)', type: 'number' },
    { key: 'actual_amount', label: 'Actual (₹)', type: 'number' },
    { key: 'settlement_fee', label: 'Fee (₹)', type: 'number' },
    { key: 'description', label: 'Description', type: 'text' },
  ],
  mandi: [
    { key: 'commodity', label: 'Commodity', type: 'text' },
    { key: 'variety', label: 'Variety', type: 'text' },
    { key: 'market', label: 'Market', type: 'text' },
    { key: 'state', label: 'State', type: 'text' },
    { key: 'min_price', label: 'Min (₹)', type: 'number' },
    { key: 'modal_price', label: 'Modal (₹)', type: 'number' },
    { key: 'max_price', label: 'Max (₹)', type: 'number' },
    { key: 'unit', label: 'Unit', type: 'text', placeholder: 'quintal/kg' },
    { key: 'date', label: 'Date', type: 'date' },
  ],
  recipes: [
    { key: 'dish_name', label: 'Dish', type: 'text' },
    { key: 'category', label: 'Category', type: 'text', placeholder: 'Food/Beverage' },
    { key: 'selling_price', label: 'Selling Price (₹)', type: 'number' },
  ],
};

function formatRow(module, r) {
  switch (module) {
    case 'suppliers':
      return {
        title: `${r.supplier_name || 'Unknown supplier'} · ${r.item_name || '—'}`,
        detail: `${r.quantity || 0} ${r.unit || ''} @ ₹${r.unit_price || 0} = ₹${r.total_amount || 0} · ${r.date || ''} · ${r.invoice_number || ''}`
      };
    case 'reconciliation':
      return {
        title: `${r.date || 'No date'} · Expected ₹${r.expected_amount || 0} / Actual ₹${r.actual_amount || 0}`,
        detail: `Fee ₹${r.settlement_fee || 0} · ${r.description || ''}`
      };
    case 'recipes':
      return {
        title: `${r.dish_name || 'Unknown dish'} (${r.category || 'Food'}) · ${(r.ingredients || []).length} ingredients`,
        detail: r.selling_price ? `Selling price ₹${r.selling_price}` : 'Ingredients: ' + (r.ingredients || []).map(i => i.name).slice(0, 4).join(', ')
      };
    case 'mandi':
      return {
        title: `${r.commodity || ''}${r.variety ? ' (' + r.variety + ')' : ''} · ${r.market || ''}`,
        detail: `Min ₹${r.min_price || 0} / Modal ₹${r.modal_price || 0} / Max ₹${r.max_price || 0} per ${r.unit || 'quintal'} · ${r.date || ''}`
      };
    default:
      return { title: JSON.stringify(r).slice(0, 80), detail: '' };
  }
}

export default PendingImports;
